import { useCallback, useEffect, useRef, useState } from 'react';
import type { PhotoItem } from '@/types/odoo';
import { pdfPageCount, pdfThumbnail, renderPdfPage, VIEWER_PAGE_PX } from '@/utils/pdfPages';

/**
 * A PDF in the installer file list, previewed and paged like a photo.
 * `utils/pdfPages.ts` explains why the pages are rendered to images.
 */

function PdfIcon() {
  return (
    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  );
}

/**
 * Page 1 as a thumbnail, rendered once the row scrolls into view so a long list
 * does not download every PDF up front. Shows the file icon until then, and
 * keeps it if the page cannot be rendered.
 */
export function PdfPlanThumb({
  url,
  name,
  loading,
  onOpen,
}: {
  url: string;
  name: string;
  loading: boolean;
  onOpen: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    const start = () => {
      pdfThumbnail(url)
        .then((thumb) => { if (!cancelled) setSrc(thumb); })
        .catch((err) => console.warn('PDF preview failed', url, err));
    };
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      start();
      return () => { cancelled = true; };
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        start();
      }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [url]);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onOpen}
      className="relative shrink-0 w-12 h-12 flex items-center justify-center rounded-lg border border-gray-200 bg-white hover:border-td-blue transition-colors cursor-pointer overflow-hidden"
      aria-label={`PDF ${name} bekijken`}
    >
      {src ? <img src={src} alt={name} className="w-full h-full object-cover" /> : <PdfIcon />}
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center bg-white/70" role="status">
          <span className="w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" />
          <span className="sr-only">PDF laden...</span>
        </span>
      )}
    </button>
  );
}

export type ViewerPage = PhotoItem & { download: { url: string; filename: string } };

/**
 * Opens a PDF as viewer pages. Page 1 opens the viewer; the rest are appended
 * as they render, so a long plan is readable without waiting for all of it.
 * Every page URL is revoked when the viewer closes or the list unmounts.
 */
export function usePdfPlanViewer() {
  const [pages, setPages] = useState<ViewerPage[] | null>(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const session = useRef(0);
  const urls = useRef<string[]>([]);

  const revokeAll = useCallback(() => {
    urls.current.forEach((u) => URL.revokeObjectURL(u));
    urls.current = [];
  }, []);

  useEffect(() => () => {
    session.current++;
    revokeAll();
  }, [revokeAll]);

  const open = useCallback(async (plan: PhotoItem, name: string) => {
    const mine = ++session.current;
    revokeAll();
    setPages(null);
    setErrorKey(null);
    setLoadingKey(plan.key);
    let rendered = 0;
    try {
      const count = await pdfPageCount(plan.url);
      for (let n = 1; n <= count; n++) {
        const url = await renderPdfPage(plan.url, n, VIEWER_PAGE_PX);
        if (mine !== session.current) {
          URL.revokeObjectURL(url);
          return;
        }
        urls.current.push(url);
        const page: ViewerPage = {
          ...plan,
          key: `${plan.key}#${n}`,
          url,
          filename: count > 1 ? `${name} — pagina ${n} van ${count}` : name,
          download: { url: plan.url, filename: name },
        };
        setPages((prev) => [...(prev ?? []), page]);
        rendered++;
        if (n === 1) setLoadingKey(null);
      }
    } catch (err) {
      if (mine !== session.current) return;
      console.error('PDF viewer failed', plan.key, err);
      setLoadingKey(null);
      // Only an error before the viewer opened needs the row message; a later
      // page failing leaves the pages that did render on screen.
      if (rendered === 0) setErrorKey(plan.key);
    }
  }, [revokeAll]);

  const close = useCallback(() => {
    session.current++;
    revokeAll();
    setPages(null);
    setLoadingKey(null);
  }, [revokeAll]);

  return { pages, loadingKey, errorKey, open, close };
}
